import * as turf from '@turf/turf';

export const metersToPixelsAtMaxZoom = (meters, latitude) =>
  meters / 0.075 / Math.cos((latitude * Math.PI) / 180);

export const getBounds = (positions) => {
  const polygons = turf.featureCollection(
    positions.map((p) =>
      turf.bboxPolygon(
        turf.bbox(
          turf.circle([p.lon, p.lat], (p.radius + p.deadband) / 1000, { steps: 4, units: 'kilometers' })
        )
      )
    )
  );
  return turf.bbox(turf.combine(polygons));
};

// mapbox-gl renders vector tiles at a 512px logical size
const TILE_SIZE = 512;

// Highest zoom worth sweeping: mapbox-streets-v8 (the source behind light/dark)
// tops out at z16, and mapbox-gl overzooms cached z16 vector tiles to render
// crisp detail beyond that offline — so caching past 16 only wastes tiles.
export const MAX_SWEEP_ZOOM = 16;

// Upper bound on tiles we're willing to cache per experience (across the swept
// zoom window). Beyond this the download would be too slow / storage-heavy, so
// we refuse it up front — the web counterpart of the native SDK's createPack
// "increase the number of map tiles" limit. ~3000 tiles ≈ a bbox up to ~20km.
export const MAX_OFFLINE_TILES = 3000;
const SWEEP_ZOOM_LEVELS = 5;

// Web-Mercator slippy-tile conversions. Tile indices may be fractional here so
// we can address the *center* of a tile (x + 0.5) when positioning the camera.
export const lngToTileX = (lng, z) => ((lng + 180) / 360) * 2 ** z;
export const latToTileY = (lat, z) => {
  const rad = (lat * Math.PI) / 180;
  return ((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * 2 ** z;
};
export const tileXToLng = (x, z) => (x / 2 ** z) * 360 - 180;
export const tileYToLat = (y, z) => {
  const n = Math.PI - (2 * Math.PI * y) / 2 ** z;
  return (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
};

/**
 * The geographic rectangle a viewport of `viewportWidth`x`viewportHeight` px
 * actually shows when fitted to `bounds` — i.e. what mapbox-gl's fitBounds
 * produces. Because the viewport has a fixed aspect ratio, the fitted frame is
 * larger than `bounds` on one axis (the "slack" you see as empty map around the
 * spots). We cache this frame (not the tight bounds) so that empty margin isn't
 * blank offline, and use it as the pan limit / min-zoom floor.
 *
 * Returns `{ frame: [w, s, e, n], zoom }` — the fractional zoom that fits.
 */
export const getOverviewFrame = (bounds, viewportWidth, viewportHeight, padding = 20) => {
  const [west, south, east, north] = bounds;
  // work in normalized web-mercator units (tile coords at z0 span [0, 1])
  const nx0 = lngToTileX(west, 0);
  const nx1 = lngToTileX(east, 0);
  const ny0 = latToTileY(north, 0); // north → smaller y
  const ny1 = latToTileY(south, 0);
  const contentW = Math.max(nx1 - nx0, 1e-12);
  const contentH = Math.max(ny1 - ny0, 1e-12);
  const cx = (nx0 + nx1) / 2;
  const cy = (ny0 + ny1) / 2;

  const availW = Math.max(1, viewportWidth - 2 * padding);
  const availH = Math.max(1, viewportHeight - 2 * padding);
  const zoom = Math.min(
    Math.log2(availW / (TILE_SIZE * contentW)),
    Math.log2(availH / (TILE_SIZE * contentH))
  );

  const scale = TILE_SIZE * 2 ** zoom; // px per normalized-mercator unit
  const halfW = viewportWidth / 2 / scale;
  const halfH = viewportHeight / 2 / scale;
  const frame = [
    tileXToLng(cx - halfW, 0),
    tileYToLat(cy + halfH, 0), // south → larger y
    tileXToLng(cx + halfW, 0),
    tileYToLat(cy - halfH, 0), // north → smaller y
  ];
  return { frame, zoom };
};

// Number of tiles covering `bbox` at zoom `z`.
const tilesAtZoom = (bbox, z) => {
  const [west, south, east, north] = bbox;
  const cols = Math.floor(lngToTileX(east, z)) - Math.floor(lngToTileX(west, z)) + 1;
  const rows = Math.floor(latToTileY(south, z)) - Math.floor(latToTileY(north, z)) + 1;
  return cols * rows;
};

// Rough count of tiles a sweep would cache: the top `SWEEP_ZOOM_LEVELS` zooms,
// which dominate the total (lower/overview levels add only a handful).
export const estimateSweepTileCount = (bbox, maxZoom = MAX_SWEEP_ZOOM) => {
  let total = 0;
  const minZoom = Math.max(0, maxZoom - (SWEEP_ZOOM_LEVELS - 1));
  for (let z = minZoom; z <= maxZoom; z += 1) total += tilesAtZoom(bbox, z);
  return total;
};

// Whether an experience's map area is too large to cache offline (see MAX_OFFLINE_TILES).
export const isAreaTooWide = (bbox) => estimateSweepTileCount(bbox) > MAX_OFFLINE_TILES;

/**
 * Build the ordered list of camera positions needed to make mapbox-gl fetch
 * every tile covering `bbox` at each integer zoom in [minZoom, maxZoom].
 *
 * The viewport (from the map container) spans `span` tiles per axis; integer
 * span-sized blocks tile the area exactly, and mapbox-gl also loads a ring of
 * tiles just beyond the viewport, so we step by a full `span` with no overlap.
 * Each returned step is a `{ zoom, center: [lng, lat] }` to `jumpTo`.
 *
 * This is the web equivalent of the native SDK's `offlineManager.createPack`:
 * instead of a tile-download API (which mapbox-gl-js does not expose), we let
 * the map itself request the tiles so its cache keys stay correct, and the
 * service worker persists them (see service-worker.js / TILES_OFFLINE_PRECACHE_NAME).
 */
export const buildSweepSteps = (bbox, minZoom, maxZoom, { width, height } = {}) => {
  const [west, south, east, north] = bbox;
  const spanX = Math.max(1, Math.floor((width || TILE_SIZE) / TILE_SIZE));
  const spanY = Math.max(1, Math.floor((height || TILE_SIZE) / TILE_SIZE));
  const strideX = spanX;
  const strideY = spanY;

  const steps = [];
  for (let z = minZoom; z <= maxZoom; z += 1) {
    const xMin = Math.floor(lngToTileX(west, z));
    const xMax = Math.floor(lngToTileX(east, z));
    // north edge maps to the smaller tile-y (y grows southward)
    const yMin = Math.floor(latToTileY(north, z));
    const yMax = Math.floor(latToTileY(south, z));

    for (let y = yMin; y <= yMax; y += strideY) {
      for (let x = xMin; x <= xMax; x += strideX) {
        // centre the span-sized viewport on this block, clamped to the range so
        // the final row/column can't drift far past the covered area
        const cx = Math.min(x + spanX / 2, xMax + 0.5);
        const cy = Math.min(y + spanY / 2, yMax + 0.5);
        steps.push({ zoom: z, center: [tileXToLng(cx, z), tileYToLat(cy, z)] });
      }
    }
  }
  return steps;
};
